/**
 * Le départ, et le retour.
 *
 * # Pourquoi `pagehide` ne suffit pas
 *
 * Toutes les enveloppes de session du produit se ferment à la fin : session de
 * studio, session de pratique d'un parcours, session d'exercice. Toutes
 * portent leurs compteurs agrégés dans cet événement de fin, et rien d'autre
 * ne les porte. Or `pagehide` **n'arrive pas** quand l'application passe en
 * arrière-plan puis est tuée par l'OS, ce qui est le sort ordinaire d'un
 * onglet sur la tablette posée sur le pupitre, c'est-à-dire sur la posture de
 * référence du produit. Une séance de vingt-cinq minutes ne laissait alors
 * aucune trace, et les sessions rapportées étaient celles des postes de bureau
 * qu'on ferme proprement : le dénominateur était biaisé contre l'appareil que
 * le produit vise.
 *
 * `visibilitychange` est le signal qui part à coup sûr. Le client d'analytique
 * l'utilise déjà pour vider sa file, pour cette raison exactement.
 *
 * # Pourquoi il faut aussi rouvrir
 *
 * Fermer sur `hidden` sans rien rouvrir échange une perte totale contre une
 * troncature systématique : basculer trente secondes sur une autre application
 * puis revenir travailler une demi-heure ne rapporterait que les trente
 * premières secondes. Le contrat est donc en deux temps, et les deux sont
 * obligatoires.
 *
 * On coupe la session plutôt que de la suspendre : une session interrompue par
 * un basculement d'application **est** une séance qui s'arrête, et son temps
 * d'arrière-plan n'est du temps de pratique pour personne. Deux séances de
 * quinze minutes séparées par un appel téléphonique décrivent mieux la réalité
 * qu'une séance de quarante minutes dont vingt seraient imaginaires.
 */
import { flush } from "./client";

export interface DepartureHandlers {
  /** Ferme l'enveloppe. Doit être idempotent : le départ peut être signalé deux fois. */
  readonly close: () => void;
  /**
   * Rouvre la même enveloppe au retour au premier plan.
   *
   * Omis quand l'appelant se rouvre tout seul — un composant React remonté par
   * la navigation, par exemple.
   */
  readonly reopen?: () => void;
}

/**
 * Arme le départ et le retour. Rend la fonction de désarmement.
 *
 * Le vidage de la file est fait ici, et il n'est pas facultatif : le client
 * s'abonne à `pagehide` et `visibilitychange` **au démarrage**, donc avant ce
 * gestionnaire. Sans ce `flush()` explicite, la fin de session serait mise en
 * file après le vidage du client, au moment précis où c'est le seul événement
 * qui reste à envoyer.
 */
export function onDeparture(handlers: DepartureHandlers): () => void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return () => {};
  }

  /** Le départ a été constaté par la mise en arrière-plan, un retour le défera. */
  let backgrounded = false;

  const depart = (): void => {
    try {
      handlers.close();
      flush();
    } catch {
      // La mesure ne peut pas casser l'application, et surtout pas sur le
      // chemin d'un départ, où personne ne pourrait plus le voir.
    }
  };

  const onPageHide = (): void => {
    backgrounded = false;
    depart();
  };

  const onVisibilityChange = (): void => {
    if (document.visibilityState === "hidden") {
      backgrounded = true;
      depart();

      return;
    }

    if (backgrounded) {
      backgrounded = false;

      try {
        handlers.reopen?.();
      } catch {
        // Idem : une réouverture ratée coûte une session, pas l'application.
      }
    }
  };

  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onVisibilityChange);

  return () => {
    window.removeEventListener("pagehide", onPageHide);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}

/**
 * Une enveloppe de session qui sait se rouvrir sur le même sujet.
 *
 * @see resumableOnDeparture
 */
export interface ResumableDeparture<S> {
  /**
   * Une session vient de s'ouvrir sur ce sujet. Mémorise-le, et arme le départ
   * si ce n'est pas déjà fait.
   *
   * ⚠️ À appeler **après** la fermeture de la session précédente, jamais avant :
   * cette fermeture oublie le sujet, et l'ordre inverse jetterait celui qu'on
   * vient de poser.
   */
  readonly opened: (subject: S) => void;
  /**
   * Une session vient de se fermer. Appelée sans condition, quelle qu'en soit
   * la raison : c'est l'enveloppe qui sait si cette fermeture est un départ,
   * puisque c'est elle qui l'a déclenchée.
   */
  readonly closed: () => void;
  /** Oublie le sujet. Réservé aux tests. */
  readonly reset: () => void;
}

/**
 * Le sujet de la session courante, mémorisé pour survivre à un aller-retour en
 * arrière-plan.
 *
 * # Ce que ça remplace
 *
 * Les trois enveloppes de session du produit — studio, exercice, parcours
 * harmonique — tenaient chacune sa propre copie de ce mécanisme : un drapeau
 * « déjà armé », un `lastSubject` de module, et une règle pour décider quand
 * l'oublier. Trois copies, et **trois comportements différents** : l'exercice
 * l'oubliait sur `reason !== "pagehide"`, le parcours harmonique sur
 * `reason === "navigation"`, et le studio ne l'oubliait jamais. Ce dernier
 * rouvrait donc une session de studio quand on revenait au premier plan depuis
 * la bibliothèque, et sa durée comptait le temps passé ailleurs.
 *
 * # Pourquoi l'appelant n'a plus à trancher
 *
 * La question « cette fermeture est-elle un départ ? » n'appartient pas à
 * l'appelant : elle appartient à ce qui a **provoqué** la fermeture. Comme
 * c'est cette enveloppe qui appelle `close`, elle le sait sans qu'on le lui
 * dise. `closed()` s'appelle donc sans argument depuis la fin de session, et
 * les trois orthographes de la même règle disparaissent avec le choix.
 *
 * L'armement n'est jamais désarmé : ces enveloppes vivent au niveau du module
 * parce que le Studio ne se démonte pas, et un désarmement n'aurait pas lieu.
 */
export function resumableOnDeparture<S>(handlers: {
  /** Ferme l'enveloppe. Doit être idempotent. */
  readonly close: () => void;
  /** Rouvre l'enveloppe sur le sujet mémorisé, au retour au premier plan. */
  readonly reopen: (subject: S) => void;
}): ResumableDeparture<S> {
  let subject: S | null = null;
  let armed = false;
  /** Vrai le temps du `close` que cette enveloppe déclenche elle-même. */
  let departing = false;

  const arm = (): void => {
    if (armed) {
      return;
    }

    armed = true;

    onDeparture({
      close: () => {
        departing = true;

        try {
          handlers.close();
        } finally {
          departing = false;
        }
      },
      // Le sujet est relu au retour, pas figé à l'armement : entre-temps le
      // Studio a pu passer à autre chose, et rouvrir sur l'ancien décrirait une
      // séance qui n'a pas lieu.
      reopen: () => {
        if (subject !== null) {
          handlers.reopen(subject);
        }
      },
    });
  };

  return {
    opened(next: S): void {
      subject = next;
      arm();
    },
    closed(): void {
      if (!departing) {
        subject = null;
      }
    },
    reset(): void {
      subject = null;
    },
  };
}
